import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';
import { withContent } from '@bubbletea/core';
import uniqueId from 'lodash/uniqueId';
import { IContent } from '../../../types';
import { IStoreState } from '../../../types/store';

import { fetchProductsData } from '../../../features/productsSlice';

import Title from '../../UI/Title';
import Push from '../../Blocks/Push';
import {
  HowToContainer,
  LoaderContainer,
  Details,
  DoublePushContainer,
  PushContainer,
  CaptionContainer,
  CaptionDescription,
  CaptionTitle,
  CaptionTitleContainer,
} from './styles';
import Loader from '../../../core/UI/Loader';
import { TripleSlashIcon } from '../../UI/Icons';
import { IMainTheme } from '../../../types/theme';
import sendEvent from '../../../utils/sendEvent';
import { IAdobeClickEvent } from '../../../types/tracking';

interface IHowToProps {
  context: IContent;
}

const HowTo = ({ context }: IHowToProps) => {
  const theme = useTheme() as IMainTheme;
  const contentSelector = useSelector((state: IStoreState) => ({
    currency: state.coreApp.currency,
    fetching: state.products.fetching,
    products: state.products.items,
  }));
  const { currency, fetching, products } = contentSelector;
  const dispatch = useDispatch();
  const { howTo } = context.googlesheet;
  const { title, description, howToItems } = howTo;

  useEffect(() => {
    if (currency) {
      const productToFetch = howToItems
        .filter((howToItem) => howToItem.blockProductId)
        .map((howToItem) => howToItem.blockProductId as string);
      dispatch(fetchProductsData(productToFetch, currency));
    }
  }, [howToItems, dispatch, currency]);

  const productFound = howToItems
    .filter((howToItem) => howToItem.blockProductId)
    .map((howToItem) => howToItem.blockProductId as string);
  const productCableDetails =
    !fetching && products.length && productFound.length === 1
      ? products.filter((product) => product.id === productFound[0])
      : [];
  const isProductInStock = productCableDetails.length && productCableDetails[0]?.inventory?.orderable;

  const impressionEventDetails: IAdobeClickEvent = {
    location: 'how to connect my guitar or bass',
    category: 'scroll',
    locationPosition: process.env['REACT_APP_PAGE_TYPE'] === 'subscriber' ? 2 : 8,
    locationDetail: isProductInStock ? 'tone cable : available' : 'tone cable : not available',
    action: 'visible',
  };

  return (
    <HowToContainer id={'howto'}>
      <div className={'section-wrapper'}>
        {fetching || (!fetching && isProductInStock) ? (
          <>
            <Title uppercase>{title}</Title>
            <Details>{description}</Details>
          </>
        ) : null}
        {!fetching && isProductInStock ? (
          <DoublePushContainer
            whileInView={'sendevent'}
            onAnimationStart={() => sendEvent(impressionEventDetails, 'impressionevent')}
            viewport={{ once: true }}
          >
            {howToItems.map((howToItem, index) => (
              <PushContainer key={`howto_push_${uniqueId()}`}>
                <CaptionContainer>
                  <CaptionTitleContainer>
                    <TripleSlashIcon color={theme.colors['Carnation']} />
                    <CaptionTitle dangerouslySetInnerHTML={{ __html: howToItem.captionTitle as string }} />
                  </CaptionTitleContainer>
                  <CaptionDescription>{howToItem.captionDescription}</CaptionDescription>
                </CaptionContainer>
                <Push item={howToItem} index={index} isProductInStock={!!isProductInStock} />
              </PushContainer>
            ))}
          </DoublePushContainer>
        ) : !fetching ? (
          <DoublePushContainer
            whileInView={'sendevent'}
            onAnimationStart={() => sendEvent(impressionEventDetails, 'impressionevent')}
            viewport={{ once: true }}
            centered
          >
            <PushContainer>
              <Title uppercase>{title}</Title>
              <Details>{description}</Details>
              <CaptionContainer>
                <CaptionTitleContainer>
                  <TripleSlashIcon color={theme.colors['Carnation']} />
                  <CaptionTitle dangerouslySetInnerHTML={{ __html: howToItems[0].captionTitle as string }} />
                </CaptionTitleContainer>
                <CaptionDescription>{howToItems[0].captionDescription}</CaptionDescription>
              </CaptionContainer>
              <CaptionContainer>
                <CaptionTitleContainer>
                  <TripleSlashIcon color={theme.colors['Carnation']} />
                  <CaptionTitle dangerouslySetInnerHTML={{ __html: howToItems[1].captionTitle as string }} />
                </CaptionTitleContainer>
                <CaptionDescription>{howToItems[1].captionDescription}</CaptionDescription>
              </CaptionContainer>
            </PushContainer>
            <PushContainer>
              <Push item={howToItems[0]} index={0} isProductInStock={!!isProductInStock} />
            </PushContainer>
          </DoublePushContainer>
        ) : (
          <LoaderContainer>
            <Loader over={false} />
          </LoaderContainer>
        )}
      </div>
    </HowToContainer>
  );
};

export default withContent(HowTo);
